import type { Editor } from '@tiptap/core';
import type { NodeSelection, Selection } from '@tiptap/pm/state';
import type { ComponentType, FC, PropsWithChildren, ReactNode, Ref } from 'react';

import type { PmMarkConverter, PmNodeConverter } from './../pm-converters/types.js';
import type { IEditorQuery } from './../ricos-content-query/index.js';
import type {
  ACTION_BUTTON_TYPE,
  AUDIO_TYPE,
  CODE_BLOCK_TYPE,
  COLLAPSIBLE_LIST_TYPE,
  DIVIDER_TYPE,
  EMOJI_TYPE,
  EXTERNAL_LINK_TYPE,
  EXTERNAL_MENTIONS_TYPE,
  FILE_UPLOAD_TYPE,
  GALLERY_TYPE,
  GIPHY_TYPE,
  HASHTAG_TYPE,
  HEADERS_MARKDOWN_TYPE,
  HEADINGS_DROPDOWN_TYPE,
  HTML_TYPE,
  IMAGE_TYPE,
  IMAGE_TYPE_LEGACY,
  INDENT_TYPE,
  LINE_SPACING_TYPE,
  LINK_BUTTON_TYPE,
  LINK_PREVIEW_TYPE,
  LINK_TYPE,
  MENTION_TYPE,
  POLL_TYPE,
  PREVIEW,
  SHAPE_TYPE,
  SPOILER_TYPE,
  TABLE_TYPE,
  TEXT_COLOR_TYPE,
  TEXT_HIGHLIGHT_TYPE,
  UNDO_REDO_TYPE,
  VERTICAL_EMBED_TYPE,
  VIDEO_TYPE,
  VIDEO_TYPE_LEGACY,
} from './../ricos-content/index.js';
import type { ModalService } from './../ricos-modals/index.js';
import type { PluginServices } from './../ricos-plugins/editorPlugins.js';
import type { MentionData as MentionPluginData, Node } from './../ricos-schema/legacy/wix/rich_content/v1/index.js';
import { Decoration_Type, Node_Type } from './../ricos-schema/legacy/wix/rich_content/v1/index.js';
import type { ToolbarButtonRenderer } from './/plugin-toolbar';
import type { LinkOptions } from './../_shared/link-modal/open-link-modal/types';
import type { SitePagesDropdownOption } from './../_shared/site-pages/types.js';
import type { FileUploadData } from './../plugin-file-upload/types.js';
import type { ContentServices } from './../toolbars-v3/types.js';

import type { PublicCommands } from './editorCommandsType.js';
import type {
  ClassNameStrategy,
  GetToolbarSettings,
  IRicosEditor,
  PluginExports,
  ResolveReferenceElement,
  RicosExtension,
  RicosPluginToolbarNamesValues,
  TFunction,
  TextDirection,
  ThemeData,
  TiptapEditor,
} from './index.js';
import type { RicosServices } from './services.js';
import type { KeyboardShortcut } from './shortcuts.js';
import type { ToolbarType } from './toolbarEnums.js';
import type { TextButton } from './toolbarTypes.js';
import type { ViewerContextualData } from './viewer.js';

export { Decoration_Type, Node_Type };

export type MentionData = { mention: MentionPluginData; trigger: string };

export type PluginMapping = {
  [type: string]: {
    component: ComponentType<PropsWithChildren>;
    classNameStrategies?: {
      size?: ClassNameStrategy;
      alignment?: ClassNameStrategy;
      textWrap?: ClassNameStrategy;
    };
    elementType?: 'inline' | 'block';
  };
};

export type PluginTypeMapper = (...args: $TSFixMe[]) => PluginMapping;

type PluginType =
  | typeof LINK_BUTTON_TYPE
  | typeof ACTION_BUTTON_TYPE
  | typeof CODE_BLOCK_TYPE
  | typeof DIVIDER_TYPE
  | typeof EMOJI_TYPE
  | typeof FILE_UPLOAD_TYPE
  | typeof GALLERY_TYPE
  | typeof GIPHY_TYPE
  | typeof HASHTAG_TYPE
  | typeof HEADERS_MARKDOWN_TYPE
  | typeof HTML_TYPE
  | typeof IMAGE_TYPE
  | typeof IMAGE_TYPE_LEGACY
  | typeof INDENT_TYPE
  | typeof LINE_SPACING_TYPE
  | typeof HEADINGS_DROPDOWN_TYPE
  | typeof SPOILER_TYPE
  | typeof EXTERNAL_LINK_TYPE
  | typeof LINK_TYPE
  | typeof LINK_PREVIEW_TYPE
  | typeof EXTERNAL_MENTIONS_TYPE
  | typeof MENTION_TYPE
  | typeof TEXT_COLOR_TYPE
  | typeof TEXT_HIGHLIGHT_TYPE
  | typeof UNDO_REDO_TYPE
  | typeof VERTICAL_EMBED_TYPE
  | typeof VIDEO_TYPE
  | typeof AUDIO_TYPE
  | typeof VIDEO_TYPE_LEGACY
  | typeof POLL_TYPE
  | typeof COLLAPSIBLE_LIST_TYPE
  | typeof TABLE_TYPE
  | typeof SHAPE_TYPE;

interface BasePluginConfig {
  type: string;
}

export type MenuGroups = 'basic' | 'advanced' | 'embed' | 'embed_wix';

type MenuConfig = {
  group?: MenuGroups;
  tags?: string;
};

export type AddButton = {
  id: string;
  icon: ComponentType;
  command: (args: {
    publicCommands: PublicCommands;
    referenceElement?: HTMLElement | null;
  }) => boolean;
  tooltip: string;
  toolbars: ToolbarType[];
  dataHook: string;
  label: string;
  menuConfig?: MenuConfig;
  shortcuts?: KeyboardShortcut[];
};

export type ToolbarButton<P = any> =
  | ({
      id: string;
      type: 'toggle' | 'modal' | 'color';
      renderer: ToolbarButtonRenderer;
      tooltip: string | null;
      label?: string;
      dataHook: string;
      command: ToolbarButtonCommand;
      attributes?: ToolbarButtonAttributes;
    } & (
      | { icon: ComponentType }
      | {
          getIcon: (args: {
            services: RicosServices;
            attributes: Record<string, any>;
          }) => ComponentType;
        }
    ))
  | {
      id: string;
      type: 'color';
      renderer: ToolbarButtonRenderer;
      attributes?: ToolbarButtonAttributes;
      tooltip: string | null;
      dataHook: string;
      command: ToolbarButtonCommand;
      getComponentProps?: GetComponentPropsFn<P>;
    }
  | {
      id: string;
      type: 'separator';
      renderer: ToolbarButtonRenderer;
      icon: ComponentType;
      attributes?: ToolbarButtonAttributes;
    }
  | ({
      id: string;
      type: 'dropdown';
      tooltip: string | null;
      label?: string;
      dataHook: string;
      renderer: ToolbarButtonRenderer;
      Component: ComponentType<P>;
      getComponentProps: GetComponentPropsFn<P>;
      command: ToolbarButtonCommand<P>;
      attributes?: ToolbarButtonAttributes;
    } & (
      | { icon: ComponentType }
      | {
          getIcon: (args: {
            services: RicosServices;
            attributes: Record<string, any>;
          }) => ComponentType;
        }
    ));

export type ToolbarButtonCommand<P = unknown> = (args: {
  referenceElement: HTMLElement | undefined;
  tiptapEditor: Editor;
  publicCommands: PublicCommands;
  attributes: $TSFixMe;
  modals: ModalService;
  isMobile: boolean;
  getComponentProps: GetComponentPropsFn<P> | undefined;
}) => void;

type GetComponentPropsFn<P> = (args: {
  tiptapEditor: Editor;
  attributes: $TSFixMe;
  publicCommands: PublicCommands;
  t: TFunction;
  modals: ModalService;
  languageDir: TextDirection;
}) => P;

export type ToolbarButtonAttributes = Record<
  string,
  | {
      resolve: (content: $TSFixMe, services: ContentServices, editor: Editor) => any;
    }
  | undefined
>;

export type FloatingToolbarButton = {
  Component: ComponentType<any>;
  priority: number;
  isVisible: (args: {
    editorQuery: IEditorQuery['query'];
    modalService: ModalService;
    editor: TiptapEditor;
    nodeId?: string;
  }) => boolean;
};

export type PluginToolbarConfig<PluginConfig extends EditorPluginConfig = Record<string, any>> = {
  names: RicosPluginToolbarNamesValues[];
  getButtons: (config: PluginConfig, services: RicosServices) => ToolbarButton[];
  isVisible?: (selection: Selection | NodeSelection, editor: IRicosEditor) => boolean;
  resolveReferenceElement?: ResolveReferenceElement;
};

export type AddButtonsCreator<PluginConfig = Record<string, any>> = (
  config: PluginConfig,
  services: RicosServices
) => AddButton[];

export interface EditorPlugin<
  PluginConfig extends EditorPluginConfig = Record<string, any>,
> extends BasePluginConfig {
  config: PluginConfig;
  tiptapExtensions: RicosExtension[];
  pmNodeConverters?: PmNodeConverter[];
  pmMarkConverters?: PmMarkConverter[];
  getAddButtons?: AddButtonsCreator<PluginConfig>;
  textButtons?: TextButton<any>[];
  getQuickActions?: ({
    pluginServices,
  }: {
    pluginServices: PluginServices;
    config: PluginConfig;
  }) => QuickAction[];
  disableOnMobile?: boolean;
  shortcuts?: KeyboardShortcut[];
  toolbar?: PluginToolbarConfig<PluginConfig>;
  floatingToolbarButton?: FloatingToolbarButton;
  exports?: Partial<PluginExports>;
  dependencies?: EditorPlugin[];
  Provider?: React.ComponentType<{ children: React.ReactNode }>;
  GlobalComponent?: React.ComponentType;
}

export type QuickAction = {
  id: string;
  title: string;
  icon: React.ComponentType;
  section: QuickActionsModalSection;
  command: ({
    editor,
    publicCommands,
    modalService,
    referenceElement,
  }: {
    editor: Editor;
    publicCommands: PublicCommands;
    modalService: ModalService;
    referenceElement?: HTMLElement;
  }) => void;
  tags: string[];
};

export interface ViewerPlugin<PluginConfig = Record<string, any>> extends BasePluginConfig {
  config: PluginConfig;
  typeMapper?: PluginTypeMapper;
  nodeViewRenderers?: NodeViewRenderers;
  nodeViewDecorators?: NodeViewDecorator<JSX.Element>[];
}

export type BasePlugin = EditorPlugin & ViewerPlugin;

export type EditorPluginCreator<PluginConfig extends Record<string, any> = Record<string, any>> = (
  config?: PluginConfig
) => EditorPlugin<PluginConfig>;

export type ViewerPluginCreator<PluginConfig> = (
  config?: PluginConfig
) => ViewerPlugin<PluginConfig>;

export interface EditorPluginConfig {
  toolbar?: {
    hidden?: string[];
    icons?: {
      [key: string]: (props: $TSFixMe) => JSX.Element;
    };
  };
  getIsVisiblePromise?: (...args: $TSFixMe[]) => Promise<boolean>;
}

export interface ViewerPluginConfig {}

export type LegacyEditorPluginConfig<
  PluginConfig extends EditorPluginConfig = Record<string, any>,
> = Partial<{
  [key in PluginType]: PluginConfig;
}> & {
  uiSettings?: UISettings;
  getToolbarSettings?: GetToolbarSettings;
  themeData?: ThemeData;

  resolveFileUrl?: (data: FileUploadData) => Promise<string>;
  [key: string]: any;
};

export type LegacyViewerPluginConfig<PluginConfig = Record<string, any>> = Partial<{
  [key in PluginType]: PluginConfig;
}> & {
  uiSettings?: UISettings;

  [PREVIEW]?: any;

  [key: string]: any;
};

export interface LinkPanelSettings {
  blankTargetToggleVisibilityFn?: () => boolean;
  nofollowRelToggleVisibilityFn?: () => boolean;
  showNewTabCheckbox?: boolean;
  showNoFollowCheckbox?: boolean;
  showNoReferrerCheckbox?: boolean;
  showSponsoredCheckbox?: boolean;
  placeholder?: string;

  dropDown?: any;
  externalPopups?: boolean;
}

export type PublicLinkType = 'section' | SitePagesDropdownOption;

export type GeneralLinkSettings = {
  /**
   *  web-address is always enabled
   * */
  enabledLinkTypes?: PublicLinkType[];
  /**
   *  Initial link options. These will be used when creating a new link and also displayed in the link panel
   * */
  initialLinkOptions?: LinkOptions;
  /**
   *  Whether to underline the link text automatically
   *  @default true
   * */
  autoUnderline?: boolean;
};

export type HistoryKeyboardShortcuts = {
  /**
   * Custom handler for undo keyboard shortcut (Mod-z)
   * Return true to prevent default behavior
   */
  undo?: () => boolean;
  /**
   * Custom handler for redo keyboard shortcut (Mod-y, Shift-Mod-z)
   * Return true to prevent default behavior
   */
  redo?: () => boolean;
};

export type HistorySettings = {
  /**
   * Callback invoked when items are added to the undo stack
   */
  onHistoryChange?: () => void;
  /**
   * Custom keyboard shortcut handlers for undo/redo.
   * Set to false to disable keyboard shortcuts entirely.
   * Provide an object with undo/redo handlers to override default behavior.
   * @default undefined (uses default keyboard shortcuts)
   */
  keyboardShortcuts?: HistoryKeyboardShortcuts | false;
};

export type UISettings = {
  linkPanel?: LinkPanelSettings;
  disableRightClick?: boolean;
  disableDownload?: boolean;
};

export interface RicosNodesViewRendererProps {
  nodes: Node[];
  forwardRef?: Ref<HTMLDivElement>;
  isRoot?: boolean;
  addAnchorsPrefix?: string;
  shouldParagraphApplyTextStyle?: boolean;
}

export type RicosNodesViewRenderer = FC<RicosNodesViewRendererProps>;

export type CustomClassNames = {
  alignment?: string;
  size?: string;
  textWrap?: string;
};

type NodeViewContainerProps = {
  node: Node;
  nodeIndex: number;
  customClassNames?: CustomClassNames;
  withHorizontalScroll?: boolean;
};

export type NodeViewContainer = FC<
  NodeViewContainerProps & {
    children:
      | ReactNode
      | ((args: {
          ContainerElement: FC<PropsWithChildren>;
          SpoilerWrapper: FC<PropsWithChildren>;
          LinkWrapper: FC<PropsWithChildren>;
        }) => ReactNode);
  }
>;

export type NodeViewRendererProps = {
  node: Node;
  nodeIndex: number;
  RicosNodesRenderer: RicosNodesViewRenderer;
  NodeViewContainer: NodeViewContainer;
  isRoot?: boolean;
  shouldParagraphApplyTextStyle?: boolean;
  SpoilerViewerWrapper?: ComponentType<{ className: unknown; width?: number }>;
};

export type NodeViewRenderer = (props: NodeViewRendererProps) => JSX.Element;

export type NodeViewRenderers = Partial<Record<Node_Type, NodeViewRenderer>>;

export type Rule = (node: Node) => boolean;

type DecorateArgs<T> = {
  node: Node;
  context: ViewerContextualData;
  element?: T;
  isRoot: boolean;
};

export type Decorate<T> = (args: DecorateArgs<T>) => T;

export type NodeViewDecorator<T> = {
  rule: Rule;
  decorate: Decorate<T>;
  isText?: boolean;
  /*
   * The priority property defines the rendering order of decorations
   * on DOM elements. Higher values render first, ensuring proper
   * decoration precedence, crucial for cases like link vs. color
   * decorations.
   * Default value is 0.
   */
  priority?: number;
};

export const quickActionsModalSections = {
  ultra: 'ultra',
  CommonlyUsed: 'QuickActionsPlugin_SectionTitle_CommonlyUsed',
  AI: 'QuickActionsPlugin_SectionTitle_AI',
  FormatLayout: 'QuickActionsPlugin_SectionTitle_FormatLayout',
  Media: 'QuickActionsPlugin_SectionTitle_Media',
  Embed: 'QuickActionsPlugin_SectionTitle_Embed',
  EmbedWix: 'QuickActionsPlugin_SectionTitle_EmbedFromWIX',
} as const;

export type QuickActionsModalSection =
  (typeof quickActionsModalSections)[keyof typeof quickActionsModalSections];
